/**
 * ensure-sources.ts — hydrate the `~/.skaile/cache/sources/` clone cache from a
 * project's `skaile.yaml` `sources:` entries.
 *
 * Called by `skaile init` (idempotent: no-op when no sources declared) and
 * `skaile install` so checking out a project pulls its source clones onto
 * the local machine.
 */

import { spawnSync } from "node:child_process";
import { existsSync, mkdirSync } from "node:fs";
import * as path from "node:path";
import {
  getGlobalCacheDir,
  type SourceEntry,
  sourceCacheKey,
  sourceShortName,
  WorkspaceYamlEditor,
} from "@skaile/workspaces/core";
import { logErr, logInfo, logOk } from "./helpers.ts";

function sourceClonePath(key: string): string {
  return path.join(getGlobalCacheDir(), key);
}

export interface EnsureResult {
  cloned: string[];
  cached: string[];
  failed: string[];
}

/** Load `sources:` from a project's `skaile.yaml`, or `null` when unavailable. */
function loadProjectSources(yamlPath: string, quiet: boolean): SourceEntry[] | null {
  try {
    return WorkspaceYamlEditor.load(yamlPath).getSources();
  } catch (err) {
    if (!quiet) {
      logErr(`Could not read ${yamlPath}: ${err instanceof Error ? err.message : String(err)}`);
    }
    return null;
  }
}

/**
 * Hydrate a single source entry into `result`: skip when already cached,
 * otherwise clone and record success/failure. Mutates `result` in place.
 */
function hydrateSourceEntry(entry: SourceEntry, quiet: boolean, result: EnsureResult): void {
  const key = sourceCacheKey(entry.url);
  const slug = sourceShortName(entry.url);
  const dest = sourceClonePath(key);
  if (existsSync(dest)) {
    result.cached.push(slug);
    return;
  }
  mkdirSync(path.dirname(dest), { recursive: true });
  const args = ["clone", entry.url, dest];
  if (entry.pin) args.splice(1, 0, "--branch", entry.pin);
  const clone = spawnSync("git", args, { stdio: quiet ? "pipe" : "inherit" });
  if (clone.status !== 0) {
    result.failed.push(slug);
    if (quiet) {
      logErr(`git clone failed for ${entry.url}`);
    }
    return;
  }
  result.cloned.push(slug);
}

/** Log a full-hydrate summary of cloned + reused-cached entries. */
function logHydrateSummary(result: EnsureResult): void {
  for (const n of result.cloned) logOk(`Cloned source "${n}".`);
  if (result.cached.length > 0) {
    logInfo(`Reused cached clones: ${result.cached.join(", ")}`);
  }
}

/**
 * Walk `projectDir/skaile.yaml` `sources:` and clone any entries whose
 * clone directory does not yet exist under `~/.skaile/cache/sources/`. Existing
 * clones are left untouched — this is hydration, not sync.
 *
 * Quiet by default: when `quiet: true` the helper only prints failures.
 * On full hydrate (`quiet: false`) it logs each cloned/cached entry.
 */
export function ensureSourcesCloned(
  projectDir: string,
  opts: { quiet?: boolean } = {},
): EnsureResult {
  const result: EnsureResult = { cloned: [], cached: [], failed: [] };
  const yamlPath = path.join(projectDir, "skaile.yaml");
  if (!existsSync(yamlPath)) return result;

  const quiet = opts.quiet ?? false;
  const sources = loadProjectSources(yamlPath, quiet);
  if (sources === null) return result;
  if (sources.length === 0) return result;

  for (const entry of sources) {
    hydrateSourceEntry(entry, quiet, result);
  }

  if (!quiet) {
    logHydrateSummary(result);
  }

  return result;
}
